// Marcas de fila — la anotación que hace el USUARIO sobre una fila para no
// perderla de vista mientras analiza.
//
// No es una selección para ejecutar una acción en lote, y no es un estado del
// dato: por eso no vive en el modelo, no viaja al backend y no se pinta con
// ninguno de los colores que en esta aplicación ya significan algo.
//
// Aquí está todo lo que se puede probar SIN render: el constructor de claves y
// el almacén observable. El hook y la UI viven en
// `components/common/MarcasFilas.tsx` y sólo enchufan esto a React.

import type { AlmacenLike } from '@/types'

import type { MarcasFilas } from '@/types'

/** Separador entre las partes de una clave.
 *
 *  Es el carácter de control «unit separator» (U+001F). Se elige precisamente
 *  porque no puede aparecer en un código de artículo, un nº de pedido, un lote
 *  ni un almacén: con un guion, la fila («AB-1», «2») y la fila («AB», «1-2»)
 *  producirían la misma clave y una marca se contagiaría a otra fila. */
export const SEP = '\u001F'

/** Prefijo de la clave de almacenamiento. */
export const PREFIJO = 'marcas.'

/**
 * Construye la clave estable de una fila a partir de lo que la identifica en el
 * negocio — nunca su índice, que cambia al ordenar, filtrar o paginar.
 *
 * Para una SUBLÍNEA se pasa primero la clave de su fila padre:
 *
 *     const cPedido = claveFila(r.purchid, r.dataareaid)
 *     const cLinea  = claveFila(cPedido, factura, l.numLinea)
 *
 * Como el resultado empieza siempre por la clave del padre, la línea 3 del
 * pedido A y la línea 3 del pedido B son claves distintas: una sublínea marcada
 * no puede aparecer marcada dentro de otro padre.
 *
 * Los huecos se conservan como parte vacía (no se descartan) para que el número
 * de partes no dependa de que un campo venga informado.
 */
export function claveFila(...partes: (string | number | null | undefined)[]): string {
  return partes.map(p => (p == null ? '' : String(p))).join(SEP)
}

/** ¿`clave` es una sublínea de `padre`? */
export function esDescendiente(clave: string, padre: string): boolean {
  return clave.startsWith(padre + SEP)
}

/** Lo mínimo que se le pide a `sessionStorage`. Se inyecta para poder probar el
 *  almacén sin navegador y para poder pasar `null` cuando no hay ninguno. */
// `AlmacenLike` vive en `types/index.ts`: lo comparten las marcas de fila y los
// filtros persistentes, y dos definiciones idénticas es una que se queda atrás.
export type { AlmacenLike }

/** Lee las marcas guardadas de un ámbito. Cualquier fallo devuelve el conjunto
 *  vacío: el almacenamiento puede lanzar (modo privado, cookies bloqueadas) y
 *  perder unas marcas nunca puede tumbar la pantalla. */
export function leerMarcas(ambito: string, storage: AlmacenLike | null): Set<string> {
  if (!storage) return new Set()
  try {
    const raw = storage.getItem(PREFIJO + ambito)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((v): v is string => typeof v === 'string'))
  } catch {
    return new Set()
  }
}

/** Guarda las marcas de un ámbito. Sin marcas se borra la entrada en vez de
 *  dejar un `[]`, para no acumular basura por cada tabla visitada. */
export function guardarMarcas(
  ambito: string,
  marcas: ReadonlySet<string>,
  storage: AlmacenLike | null,
): void {
  if (!storage) return
  try {
    if (marcas.size === 0) storage.removeItem(PREFIJO + ambito)
    else storage.setItem(PREFIJO + ambito, JSON.stringify([...marcas]))
  } catch {
    /* sin almacenamiento las marcas siguen funcionando en memoria */
  }
}

/** Oyentes de «cualquier cambio», frente a los de una clave concreta. */
const GLOBAL = Symbol('marcas.global')

/**
 * Almacén observable de marcas de un ámbito (una tabla, una pantalla).
 *
 * Vive FUERA de React a propósito. Si el conjunto de marcas fuese estado de la
 * página, marcar una fila re-renderizaría la tabla entera; aquí cada fila se
 * suscribe SÓLO a su propia clave, así que marcar una fila despierta a esa fila
 * y a nadie más. El recuento de la franja se suscribe aparte, por lo global.
 *
 * El conjunto se REEMPLAZA en cada cambio en vez de mutarse: así `todas()` es
 * una instantánea válida para `useSyncExternalStore`, que compara por identidad.
 * El coste es lineal en el número de marcas (decenas), no en el de filas.
 */
export class AlmacenMarcas implements MarcasFilas {
  private claves: Set<string>
  private oyentes = new Map<string | symbol, Set<() => void>>()

  constructor(
    readonly ambito: string,
    private readonly storage: AlmacenLike | null = null,
  ) {
    this.claves = leerMarcas(ambito, storage)
  }

  tiene(clave: string): boolean {
    return this.claves.has(clave)
  }

  total(): number {
    return this.claves.size
  }

  todas(): ReadonlySet<string> {
    return this.claves
  }

  alternar(clave: string): void {
    const siguiente = new Set(this.claves)
    if (siguiente.has(clave)) siguiente.delete(clave)
    else siguiente.add(clave)
    this.aplicar(siguiente, [clave])
  }

  quitar(clave: string): void {
    if (!this.claves.has(clave)) return
    const siguiente = new Set(this.claves)
    siguiente.delete(clave)
    this.aplicar(siguiente, [clave])
  }

  limpiar(): void {
    if (this.claves.size === 0) return
    const afectadas = [...this.claves]
    this.aplicar(new Set(), afectadas)
  }

  /**
   * `clave === null` suscribe a cualquier cambio (el recuento de la franja).
   * Devuelve la función de baja, tal y como espera `useSyncExternalStore`.
   */
  suscribir(clave: string | null, alCambiar: () => void): () => void {
    const id: string | symbol = clave ?? GLOBAL
    let set = this.oyentes.get(id)
    if (!set) { set = new Set(); this.oyentes.set(id, set) }
    set.add(alCambiar)
    return () => {
      const actual = this.oyentes.get(id)
      if (!actual) return
      actual.delete(alCambiar)
      if (actual.size === 0) this.oyentes.delete(id)
    }
  }

  private aplicar(siguiente: Set<string>, afectadas: string[]) {
    this.claves = siguiente
    guardarMarcas(this.ambito, siguiente, this.storage)
    for (const clave of afectadas) {
      const set = this.oyentes.get(clave)
      if (set) for (const fn of [...set]) fn()
    }
    const globales = this.oyentes.get(GLOBAL)
    if (globales) for (const fn of [...globales]) fn()
  }
}
